import React, { useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import Icon from "components/AppIcon";

const revenueData = [
  { month: "Sep", retail: 42000, tech: 68000, manufacturing: 55000, financial: 91000, black_market: 38000 },
  { month: "Oct", retail: 45000, tech: 72000, manufacturing: 58000, financial: 88000, black_market: 42000 },
  { month: "Nov", retail: 48000, tech: 75000, manufacturing: 61000, financial: 95000, black_market: 45000 },
  { month: "Dec", retail: 52000, tech: 80000, manufacturing: 64000, financial: 102000, black_market: 50000 },
  { month: "Jan", retail: 49000, tech: 78000, manufacturing: 62000, financial: 98000, black_market: 47000 },
  { month: "Feb", retail: 55000, tech: 85000, manufacturing: 68000, financial: 108000, black_market: 53000 },
];

const sectorColors = {
  retail: "var(--color-primary)",
  tech: "#A78BFA",
  manufacturing: "var(--color-accent)",
  financial: "var(--color-success)",
  black_market: "var(--color-error)",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border p-3 text-xs" style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}>
      <div className="font-heading text-xs text-foreground mb-2">{label}</div>
      {payload?.map((entry) => (
        <div key={entry?.dataKey} className="flex items-center justify-between gap-4 mb-1">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ background: entry?.color }} />
            <span className="font-caption text-muted-foreground capitalize">{entry?.dataKey?.replace("_", " ")}</span>
          </div>
          <span className="font-caption font-semibold" style={{ color: entry?.color }}>${entry?.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

const RevenueChart = ({ ownedSectors = [] }) => {
  const [activeFilter, setActiveFilter] = useState("all");

  const visibleSectors = activeFilter === "all"
    ? Object.keys(sectorColors)?.filter(s => ownedSectors?.includes(s))
    : [activeFilter];

  return (
    <div className="rounded-xl border p-4 md:p-5" style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Icon name="TrendingUp" size={16} color="var(--color-primary)" />
          <h3 className="font-heading text-sm font-semibold text-foreground">Revenue Performance</h3>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => setActiveFilter("all")}
            className="px-2.5 py-1 rounded-md font-caption text-xs transition-all duration-200"
            style={{ background: activeFilter === "all" ? "rgba(0,217,255,0.15)" : "transparent", color: activeFilter === "all" ? "var(--color-primary)" : "var(--color-muted-foreground)", border: `1px solid ${activeFilter === "all" ? "rgba(0,217,255,0.3)" : "transparent"}` }}
          >
            All
          </button>
          {ownedSectors?.map(sector => (
            <button
              key={sector}
              onClick={() => setActiveFilter(sector)}
              className="px-2.5 py-1 rounded-md font-caption text-xs transition-all duration-200 capitalize"
              style={{ background: activeFilter === sector ? `${sectorColors?.[sector]}20` : "transparent", color: activeFilter === sector ? sectorColors?.[sector] : "var(--color-muted-foreground)", border: `1px solid ${activeFilter === sector ? `${sectorColors?.[sector]}40` : "transparent"}` }}
            >
              {sector?.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>
      <div className="w-full h-48 md:h-64" aria-label="Business Revenue Area Chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={revenueData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <defs>
              {visibleSectors?.map(sector => (
                <linearGradient key={sector} id={`grad_${sector}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={sectorColors?.[sector]} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={sectorColors?.[sector]} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="month" tick={{ fill: "var(--color-muted-foreground)", fontSize: 11, fontFamily: "var(--font-caption)" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "var(--color-muted-foreground)", fontSize: 11, fontFamily: "var(--font-caption)" }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000)?.toFixed(0)}k`} width={45} />
            <Tooltip content={<CustomTooltip />} />
            {visibleSectors?.map(sector => (
              <Area key={sector} type="monotone" dataKey={sector} stroke={sectorColors?.[sector]} strokeWidth={2} fill={`url(#grad_${sector})`} dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RevenueChart;